import boardsAxios from "../../../utils/boardsAxios";

// Thin promise wrappers around every Phase 1 boards endpoint. Pages call
// these directly and handle their own loading/toast state — most boards
// mutations are too fine-grained to round-trip through redux.

const unwrap = (p) =>
  p.then((res) => {
    if (!res?.data?.status) {
      const err = new Error(res?.data?.message || "Request failed");
      err.payload = res?.data;
      throw err;
    }
    return res.data;
  });

export const listWorkspaces = () => unwrap(boardsAxios.get("v1/workspaces"));

export const listBoards = (filters = {}) =>
  unwrap(boardsAxios.post("v1/boards/list", filters));

export const createBoard = (body) => unwrap(boardsAxios.post("v1/boards", body));

export const getBoard = (boardId) => unwrap(boardsAxios.get(`v1/boards/${boardId}`));

export const updateBoard = (boardId, body) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}`, body));

export const deleteBoard = (boardId) => unwrap(boardsAxios.delete(`v1/boards/${boardId}`));

export const addBoardMember = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/members`, body));

export const removeBoardMember = (boardId, userId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/members/${userId}`));

export const listColumnTypes = () => unwrap(boardsAxios.get("v1/column-types"));

export const createColumn = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/columns`, body));

export const updateColumn = (boardId, colId, body) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}/columns/${colId}`, body));

export const deleteColumn = (boardId, colId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/columns/${colId}`));

export const createGroup = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/groups`, body));

export const updateGroup = (boardId, groupId, body) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}/groups/${groupId}`, body));

export const deleteGroup = (boardId, groupId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/groups/${groupId}`));

export const createItem = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/items`, body));

// Sugar for nested items — bugs/features under a module. Same endpoint as
// createItem, with parent_item_id wired in.
export const createSubItem = (boardId, parentId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/items`, { ...body, parent_item_id: parentId }));

export const listItems = (boardId, body = {}) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/items/list`, body));

export const getItem = (boardId, itemId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/items/${itemId}`));

export const updateItem = (boardId, itemId, body) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}/items/${itemId}`, body));

export const deleteItem = (boardId, itemId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/items/${itemId}`));

export const setCellValue = (boardId, itemId, colId, value) =>
  unwrap(boardsAxios.put(`v1/boards/${boardId}/items/${itemId}/column/${colId}`, { value }));

export const listUpdates = (boardId, itemId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/items/${itemId}/updates`));

export const createUpdate = (boardId, itemId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/items/${itemId}/updates`, { body }));

export const deleteUpdate = (boardId, itemId, updateId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/items/${itemId}/updates/${updateId}`));

export const listActivity = (boardId, params = {}) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/activity`, { params }));

// ── Board defaults (super-admin) ───────────────────────────────────
export const getBoardDefaults = () => unwrap(boardsAxios.get("v1/board-defaults"));
export const updateBoardDefaults = (body) => unwrap(boardsAxios.put("v1/board-defaults", body));
export const resetBoardDefaults = () => unwrap(boardsAxios.post("v1/board-defaults/reset"));
export const backfillAllBoards = () => unwrap(boardsAxios.post("v1/board-defaults/backfill-all"));
export const applyDefaultsToBoard = (boardId) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/apply-defaults`));

// ── Automations ────────────────────────────────────────────────────
export const listAutomations = (boardId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/automations`));
export const createAutomation = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/automations`, body));
export const updateAutomation = (boardId, id, body) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}/automations/${id}`, body));
export const deleteAutomation = (boardId, id) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/automations/${id}`));
export const listAutomationRuns = (boardId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/automation-runs`));

// ── Notifications ─────────────────────────────────────────────────
export const listNotifications = (params = {}) =>
  unwrap(boardsAxios.get("v1/notifications", { params }));
export const markNotificationRead = (id) =>
  unwrap(boardsAxios.patch(`v1/notifications/${id}/read`));
export const markAllNotificationsRead = () =>
  unwrap(boardsAxios.patch("v1/notifications/read-all"));

// ── Attachments ─────────────────────────────────────────────────
export const uploadAttachment = (boardId, itemId, colId, file) => {
  const form = new FormData();
  form.append("file", file);
  return unwrap(
    boardsAxios.post(
      `v1/boards/${boardId}/items/${itemId}/column/${colId}/attachments`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  );
};
export const listAttachmentsForItem = (boardId, itemId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/items/${itemId}/attachments`));
export const deleteAttachment = (boardId, attId) =>
  unwrap(boardsAxios.delete(`v1/boards/${boardId}/attachments/${attId}`));
export const attachmentDownloadUrl = (boardId, attId) => {
  const base = boardsAxios.defaults.baseURL.replace(/\/$/, "");
  return `${base}/v1/boards/${boardId}/attachments/${attId}/download`;
};
// Upload an image/file pasted or dropped into the comment composer. Backend
// stores a column-less Attachment; the Update body references it via
// `![alt](attId)` which MentionText renders inline.
export const uploadInlineAttachment = (boardId, itemId, file) => {
  const form = new FormData();
  form.append("file", file);
  return unwrap(
    boardsAxios.post(
      `v1/boards/${boardId}/items/${itemId}/update-attachments`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
  );
};

// ── CSV import / export ─────────────────────────────────────────
export const importCsv = (boardId, csv, group_id) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/items/import-csv`, { csv, group_id }));
export const exportCsvUrl = (boardId) => {
  const base = boardsAxios.defaults.baseURL.replace(/\/$/, "");
  return `${base}/v1/boards/${boardId}/items/export-csv`;
};

// Pulls CSV via the configured axios instance (carries the urltoken cookie)
// and triggers a browser download via a Blob URL. Used instead of a bare
// link because cross-port cookies don't reliably attach to <a download>.
// ── Workspaces (full CRUD) ─────────────────────────────────────
export const listWorkspacesAll = () => unwrap(boardsAxios.get("v1/workspaces"));
export const createWorkspace = (body) => unwrap(boardsAxios.post("v1/workspaces", body));
export const updateWorkspace = (id, body) => unwrap(boardsAxios.patch(`v1/workspaces/${id}`, body));
export const deleteWorkspace = (id) => unwrap(boardsAxios.delete(`v1/workspaces/${id}`));

// ── Share links ────────────────────────────────────────────────
export const listShareLinks = (boardId) =>
  unwrap(boardsAxios.get(`v1/boards/${boardId}/share-links`));
export const createShareLink = (boardId, body) =>
  unwrap(boardsAxios.post(`v1/boards/${boardId}/share-links`, body));
export const revokeShareLink = (boardId, id) =>
  unwrap(boardsAxios.patch(`v1/boards/${boardId}/share-links/${id}/revoke`));
// Public endpoint — no auth. Returns 200 with board+items or 404/410.
export const fetchSharedBoard = (token) =>
  unwrap(boardsAxios.get(`share/${encodeURIComponent(token)}`));

// ── Dashboards ─────────────────────────────────────────────────
export const listDashboards = (workspace_id) =>
  unwrap(boardsAxios.get("v1/dashboards", { params: workspace_id ? { workspace_id } : {} }));
export const getDashboard = (id) => unwrap(boardsAxios.get(`v1/dashboards/${id}`));
export const createDashboard = (body) => unwrap(boardsAxios.post("v1/dashboards", body));
export const updateDashboard = (id, body) => unwrap(boardsAxios.patch(`v1/dashboards/${id}`, body));
export const deleteDashboard = (id) => unwrap(boardsAxios.delete(`v1/dashboards/${id}`));
export const getWidgetData = (dashboardId, widgetId) =>
  unwrap(boardsAxios.get(`v1/dashboards/${dashboardId}/widgets/${widgetId}/data`));

export const downloadCsv = async (boardId, filename) => {
  const res = await boardsAxios.get(`v1/boards/${boardId}/items/export-csv`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "board.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
